<template>
  <div class="invoice-page py-3">
    <header class="invoice-toolbar">
      <div class="invoice-toolbar__title">
        <span class="invoice-toolbar__accent" aria-hidden="true"></span>
        <div>
          <h1>Invoice &amp; Order Numbers</h1>
          <p>Manage prefixes and counters for every order, invoice, and credit-note series</p>
        </div>
      </div>
      <div class="invoice-toolbar__actions">
        <span v-if="lastUpdatedAt && !isLoading" class="invoice-updated">
          Updated {{ lastUpdatedLabel }}
        </span>
        <CButton
          size="sm"
          class="refresh-btn"
          :class="{ 'is-loading': isLoading }"
          :disabled="isLoading"
          @click="loadData"
        >
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="refresh-btn__icon" aria-hidden="true">
            <polyline points="23 4 23 10 17 10"/>
            <polyline points="1 20 1 14 7 14"/>
            <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
          </svg>
          <span>{{ isLoading ? 'Refreshing…' : 'Refresh' }}</span>
        </CButton>
      </div>
    </header>

    <CAlert v-if="errorMessage" color="danger" show class="invoice-alert">{{ errorMessage }}</CAlert>
    <CAlert v-if="successMessage" color="success" show class="invoice-alert">{{ successMessage }}</CAlert>

    <!-- Counter-mismatch warning. Surfaces the same problem the inbox
         tracks server-side, but inline on the page where admins go to
         fix it. The series rows below highlight which ones are behind. -->
    <div v-if="behindSeries.length" class="counter-warning">
      <div class="counter-warning__head">
        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
          <line x1="12" y1="9" x2="12" y2="13"/>
          <line x1="12" y1="17" x2="12.01" y2="17"/>
        </svg>
        <div>
          <strong>{{ behindSeries.length }} {{ behindSeries.length === 1 ? 'series has' : 'series have' }} a counter behind the highest number in use.</strong>
          <p>The next document number will collide with an existing record (or land blank). Edit the affected series below and bump the counter to at least the highest used value.</p>
        </div>
      </div>
      <ul class="counter-warning__list">
        <li v-for="row in behindSeries" :key="row.id">
          <code>{{ row.prefix }}</code>
          <span class="counter-warning__name">{{ row.name }}</span>
          <span class="counter-warning__gap">counter {{ row.counter }} · highest {{ row.actual_max }} (behind {{ row.gap }})</span>
        </li>
      </ul>
    </div>

    <div class="summary-tiles">
      <div class="summary-tile summary-tile--total">
        <span class="summary-tile__label">Total Series</span>
        <span class="summary-tile__value">{{ series.length }}</span>
      </div>
      <div class="summary-tile summary-tile--active">
        <span class="summary-tile__label">In Use</span>
        <span class="summary-tile__value">{{ activeSeriesCount }}</span>
      </div>
      <div class="summary-tile summary-tile--issued">
        <span class="summary-tile__label">Total Issued</span>
        <span class="summary-tile__value">{{ totalIssued.toLocaleString('en-IN') }}</span>
      </div>
    </div>

    <BaseTable
      :items="filteredSeries"
      :columns="columns"
      title="Document Series"
      accent="linear-gradient(180deg, #0e5b46, #16a07a)"
      :loading="isLoading"
      :default-sort="{ column: 'id', direction: 'asc' }"
      striped
      hover
      bordered
      sticky-header
      class="invoice-table"
    >
      <template #toolbar>
        <div class="invoice-search-wrap">
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="invoice-search-icon" aria-hidden="true">
            <circle cx="11" cy="11" r="7"/>
            <line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </svg>
          <input
            v-model.trim="searchTerm"
            type="text"
            class="invoice-input"
            placeholder="Search by name or prefix…"
          />
          <button
            v-if="searchTerm"
            type="button"
            class="invoice-search-clear"
            aria-label="Clear search"
            @click="searchTerm = ''"
          >×</button>
        </div>
      </template>

      <template #cell-name="{ row }">
        <div class="series-name">
          <span :class="['series-pill', orgClass(row.name)]">{{ orgLabel(row.name) }}</span>
          <span class="series-name__text">{{ row.name }}</span>
        </div>
      </template>

      <template #cell-prefix="{ row }">
        <code class="prefix-chip">{{ row.prefix || '—' }}</code>
      </template>

      <template #cell-counter="{ row }">
        <div class="counter-cell">
          <span class="counter-value">{{ Number(row.counter || 0).toLocaleString('en-IN') }}</span>
          <span
            v-if="row.is_behind"
            class="counter-flag"
            :title="`Highest in use: ${row.actual_max}. Counter is behind by ${row.gap}.`"
          >behind {{ row.gap }}</span>
        </div>
      </template>

      <template #cell-next="{ row }">
        <code :class="['next-chip', { 'next-chip--collide': row.is_behind }]">{{ buildNextNumber(row) }}</code>
      </template>

      <template #cell-actions="{ row }">
        <button type="button" class="edit-btn" @click="openEdit(row)">
          <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
          </svg>
          <span>Edit</span>
        </button>
      </template>

      <template #empty>
        <div v-if="!series.length" class="invoice-empty">
          <div class="invoice-empty__title">No document series configured</div>
          <div class="invoice-empty__hint">Series rows are seeded into <code>document_series2</code> by the backend.</div>
        </div>
        <div v-else class="invoice-empty">
          <div class="invoice-empty__title">No matches for "{{ searchTerm }}"</div>
          <div class="invoice-empty__hint">
            Try a different name, or
            <button type="button" class="invoice-empty__link" @click="searchTerm = ''">clear search</button>.
          </div>
        </div>
      </template>
    </BaseTable>

    <CModal
      :show.sync="editModal.show"
      :title="editModal.title"
      :centered="true"
      size=""
      class="invoice-modal"
    >
      <form v-if="editModal.row" class="invoice-form" @submit.prevent="saveEdit">
        <div class="invoice-form__meta">
          <span class="invoice-form__meta-label">Series</span>
          <span class="invoice-form__meta-value">{{ editModal.row.name }}</span>
        </div>

        <div class="form-field">
          <label for="series-prefix" class="form-label">Prefix</label>
          <input
            id="series-prefix"
            v-model="editForm.prefix"
            type="text"
            class="form-input"
            autocomplete="off"
            :disabled="isSaving"
            :class="{ 'has-error': fieldErrors.prefix }"
            @input="clearFieldError('prefix')"
          />
          <span v-if="fieldErrors.prefix" class="form-hint form-hint--error">{{ fieldErrors.prefix }}</span>
          <span v-else class="form-hint">Appears at the start of every generated document number.</span>
        </div>

        <div class="form-field">
          <label for="series-counter" class="form-label">Counter</label>
          <input
            id="series-counter"
            v-model.number="editForm.counter"
            type="number"
            min="0"
            step="1"
            class="form-input"
            autocomplete="off"
            :disabled="isSaving"
            :class="{ 'has-error': fieldErrors.counter }"
            @input="clearFieldError('counter')"
          />
          <span v-if="fieldErrors.counter" class="form-hint form-hint--error">{{ fieldErrors.counter }}</span>
          <span v-else class="form-hint">Last issued number. The next document will use counter + 1.</span>
        </div>

        <div class="invoice-form__preview">
          <span class="invoice-form__preview-label">Next document number</span>
          <code class="invoice-form__preview-value">{{ previewNextNumber }}</code>
        </div>

        <div v-if="editAudit.actual_max > 0" class="invoice-form__audit">
          <span class="invoice-form__audit-label">Highest number already issued for this prefix</span>
          <code class="invoice-form__audit-value">{{ editModal.row.prefix }}{{ editAudit.actual_max }}</code>
        </div>

        <div v-if="editWouldCollide" class="invoice-form__collide">
          <strong>Warning:</strong> the counter you entered is below the highest number already in use ({{ editAudit.actual_max }}). Saving will cause the next issued document to collide or appear blank. Set the counter to at least {{ editAudit.actual_max }}.
        </div>
      </form>

      <template #footer>
        <button type="button" class="cancel-btn" :disabled="isSaving" @click="closeEdit">Cancel</button>
        <button type="button" class="submit-btn" :disabled="isSaving || !canSubmit" @click="saveEdit">
          <CSpinner v-if="isSaving" size="sm" class="submit-btn__spinner" />
          <span>{{ isSaving ? 'Saving…' : 'Save changes' }}</span>
        </button>
      </template>
    </CModal>
  </div>
</template>

<script>
import axios from 'axios'
import BaseTable from '@/components/BaseTable.vue'

export default {
  name: 'SettingsInvoice',
  components: { BaseTable },
  data () {
    return {
      isLoading: false,
      isSaving: false,
      errorMessage: '',
      successMessage: '',
      searchTerm: '',
      series: [],
      lastUpdatedAt: null,
      columns: [
        { key: 'id', label: 'Id', width: '60px', align: 'right' },
        { key: 'name', label: 'Series' },
        { key: 'prefix', label: 'Prefix', width: '180px' },
        { key: 'counter', label: 'Counter', width: '120px', align: 'right' },
        { key: 'next', label: 'Next number', width: '200px' },
        { key: 'actions', label: '', width: '88px', align: 'right', sortable: false }
      ],
      editModal: {
        show: false,
        row: null,
        title: 'Edit series'
      },
      editForm: {
        prefix: '',
        counter: 0
      },
      fieldErrors: {}
    }
  },
  computed: {
    filteredSeries () {
      const term = this.searchTerm.toLowerCase()
      if (!term) return this.series
      return this.series.filter((row) =>
        [row.id, row.name, row.prefix]
          .filter((value) => value !== null && value !== undefined && value !== '')
          .some((value) => String(value).toLowerCase().includes(term))
      )
    },
    activeSeriesCount () {
      return this.series.filter((row) => Number(row.counter || 0) > 0).length
    },
    totalIssued () {
      return this.series.reduce((sum, row) => sum + Number(row.counter || 0), 0)
    },
    lastUpdatedLabel () {
      if (!this.lastUpdatedAt) return ''
      const hours = String(this.lastUpdatedAt.getHours()).padStart(2, '0')
      const minutes = String(this.lastUpdatedAt.getMinutes()).padStart(2, '0')
      return `${hours}:${minutes}`
    },
    canSubmit () {
      const counter = Number(this.editForm.counter)
      return Number.isFinite(counter) && counter >= 0
    },
    previewNextNumber () {
      const prefix = this.editForm.prefix || ''
      const counter = Number(this.editForm.counter)
      if (!Number.isFinite(counter)) return `${prefix}—`
      return `${prefix}${counter + 1}`
    },
    // Series whose stored counter is below the highest number actually
    // used in `orders` for that prefix. The backend includes
    // `is_behind`, `actual_max`, and `gap` on every series row via
    // DocumentSeries2Controller::auditSeries().
    behindSeries () {
      return this.series.filter((row) => row.is_behind)
    },
    editAudit () {
      const row = this.editModal.row
      return {
        actual_max: Number(row?.actual_max || 0),
        column: row?.audit_column || null
      }
    },
    editWouldCollide () {
      const counter = Number(this.editForm.counter)
      if (!Number.isFinite(counter)) return false
      return counter < this.editAudit.actual_max
    }
  },
  created () {
    this.loadData()
  },
  methods: {
    loadData () {
      this.isLoading = true
      this.errorMessage = ''

      const token = localStorage.getItem('api_token')

      axios.get(`${this.$apiAdress}/api/document-series?token=${token}`)
        .then((response) => {
          this.series = this.extractSeries(response.data)
          this.lastUpdatedAt = new Date()
        })
        .catch((error) => {
          console.log(error)
          this.errorMessage = error?.response?.data?.message || 'Unable to load invoice series right now.'
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    extractSeries (data) {
      if (Array.isArray(data)) return data
      if (Array.isArray(data?.data)) return data.data
      if (Array.isArray(data?.series)) return data.series
      return []
    },
    buildNextNumber (row) {
      const prefix = row.prefix || ''
      const counter = Number(row.counter || 0) + 1
      return `${prefix}${counter}`
    },
    openEdit (row) {
      this.fieldErrors = {}
      this.errorMessage = ''
      this.successMessage = ''
      this.editModal.row = row
      this.editModal.title = `Edit · ${row.name}`
      this.editForm.prefix = row.prefix || ''
      this.editForm.counter = Number(row.counter || 0)
      this.editModal.show = true
    },
    closeEdit () {
      if (this.isSaving) return
      this.editModal.show = false
      this.editModal.row = null
    },
    clearFieldError (field) {
      if (this.fieldErrors[field]) {
        this.$delete(this.fieldErrors, field)
      }
    },
    validateEdit () {
      const errors = {}
      const counter = Number(this.editForm.counter)
      if (!Number.isFinite(counter) || counter < 0) {
        errors.counter = 'Counter must be a non-negative whole number.'
      } else if (!Number.isInteger(counter)) {
        errors.counter = 'Counter must be a whole number.'
      }
      if (this.editForm.prefix && this.editForm.prefix.length > 64) {
        errors.prefix = 'Prefix must be 64 characters or fewer.'
      }
      this.fieldErrors = errors
      return Object.keys(errors).length === 0
    },
    saveEdit () {
      if (!this.editModal.row) return
      if (!this.validateEdit()) return

      this.isSaving = true
      this.errorMessage = ''
      this.successMessage = ''

      const id = this.editModal.row.id
      const token = localStorage.getItem('api_token')
      const payload = {
        prefix: this.editForm.prefix || '',
        counter: Number(this.editForm.counter)
      }

      axios.put(`${this.$apiAdress}/api/document-series/${id}?token=${token}`, payload)
        .then((response) => {
          const validationErrors = response.data?.error
          if (validationErrors && typeof validationErrors === 'object') {
            this.fieldErrors = this.flattenValidationErrors(validationErrors)
            this.errorMessage = 'Please fix the errors below.'
            return
          }

          const updated = response.data?.data
          if (updated && updated.id) {
            const index = this.series.findIndex((row) => row.id === updated.id)
            if (index !== -1) {
              this.$set(this.series, index, { ...this.series[index], ...updated })
            }
          }

          this.successMessage = response.data?.message || 'Series updated successfully.'
          this.editModal.show = false
          this.editModal.row = null
        })
        .catch((error) => {
          console.log(error)
          const data = error?.response?.data
          if (data?.error && typeof data.error === 'object') {
            this.fieldErrors = this.flattenValidationErrors(data.error)
          }
          this.errorMessage = data?.message || 'Unable to update series. You may not have permission.'
        })
        .finally(() => {
          this.isSaving = false
        })
    },
    flattenValidationErrors (errors) {
      const flat = {}
      Object.keys(errors).forEach((key) => {
        const value = errors[key]
        flat[key] = Array.isArray(value) ? value[0] : String(value)
      })
      return flat
    },
    orgLabel (name) {
      const text = String(name || '').toUpperCase()
      if (text.startsWith('MAP')) return 'MAP'
      if (text.startsWith('MDI')) return 'MDI'
      if (text.startsWith('MVP')) return 'MVP'
      return '—'
    },
    orgClass (name) {
      const text = String(name || '').toUpperCase()
      if (text.startsWith('MAP')) return 'series-pill--green'
      if (text.startsWith('MDI')) return 'series-pill--blue'
      if (text.startsWith('MVP')) return 'series-pill--violet'
      return 'series-pill--gray'
    }
  }
}
</script>

<style scoped>
.invoice-page {
  padding: 0.25rem 0;
}

/* ── Toolbar ── */
.invoice-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.75rem;
  padding: 0.25rem 0.25rem 1rem;
}

.invoice-toolbar__title {
  display: flex;
  align-items: center;
  gap: 0.65rem;
}

.invoice-toolbar__accent {
  width: 4px;
  height: 36px;
  border-radius: 4px;
  background: linear-gradient(180deg, #0e5b46 0%, #16a07a 100%);
  flex-shrink: 0;
}

.invoice-toolbar__title h1 {
  margin: 0;
  font-size: 1.15rem;
  font-weight: 700;
  color: #1f2a44;
  letter-spacing: -0.01em;
  line-height: 1.2;
}

.invoice-toolbar__title p {
  margin: 0;
  font-size: 0.78rem;
  color: #6b7280;
}

.invoice-toolbar__actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.65rem;
}

.invoice-updated {
  font-size: 0.75rem;
  color: #6b7280;
  font-weight: 500;
}

.invoice-toolbar__actions >>> .btn.refresh-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  height: 34px;
  padding: 0 0.85rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.82rem;
  background: #ffffff;
  border: 1px solid #e5e9f2;
  color: #1f2a44;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
  transition: border-color 0.15s ease, background 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;
}

.invoice-toolbar__actions >>> .btn.refresh-btn:hover:not(:disabled) {
  border-color: #16a07a;
  color: #0e5b46;
  background: #f5fbf8;
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.12);
}

.invoice-toolbar__actions >>> .btn.refresh-btn:disabled {
  opacity: 1;
  background: #f8fafc;
  color: #4b5563;
  cursor: progress;
}

.refresh-btn__icon {
  flex-shrink: 0;
  display: block;
  color: #16a07a;
  transition: transform 0.4s ease;
}

.invoice-toolbar__actions >>> .btn.refresh-btn:hover:not(:disabled) .refresh-btn__icon {
  transform: rotate(60deg);
}

.invoice-toolbar__actions >>> .btn.refresh-btn.is-loading .refresh-btn__icon {
  animation: refresh-spin 0.9s linear infinite;
  color: #16a07a;
}

@keyframes refresh-spin {
  to { transform: rotate(360deg); }
}

/* ── Alerts ── */
.invoice-alert {
  margin: 0 0 0.75rem;
  border-radius: 10px;
}

/* ── Counter-mismatch warning ── */
.counter-warning {
  background: #fff7ed;
  border: 1px solid #fdba74;
  border-left: 4px solid #c2410c;
  border-radius: 10px;
  padding: 0.85rem 1rem;
  margin-bottom: 0.85rem;
  color: #7c2d12;
}

.counter-warning__head {
  display: flex;
  align-items: flex-start;
  gap: 0.65rem;
}

.counter-warning__head svg {
  flex-shrink: 0;
  color: #c2410c;
  margin-top: 0.1rem;
}

.counter-warning__head strong {
  display: block;
  font-size: 0.88rem;
  color: #7c2d12;
  letter-spacing: -0.01em;
}

.counter-warning__head p {
  margin: 0.2rem 0 0;
  font-size: 0.78rem;
  color: #9a3412;
}

.counter-warning__list {
  list-style: none;
  margin: 0.55rem 0 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
}

.counter-warning__list li {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.78rem;
  background: #ffedd5;
  padding: 0.35rem 0.55rem;
  border-radius: 6px;
}

.counter-warning__list code {
  background: #ffffff;
  color: #7c2d12;
  padding: 0.1rem 0.4rem;
  border-radius: 4px;
  font-weight: 700;
  font-size: 0.74rem;
}

.counter-warning__name {
  font-weight: 600;
  color: #7c2d12;
}

.counter-warning__gap {
  color: #9a3412;
  font-variant-numeric: tabular-nums;
}

/* ── Summary tiles ── */
.summary-tiles {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.summary-tile {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  padding: 0.85rem 1rem;
  border-radius: 12px;
  background: #ffffff;
  border: 1px solid #e5e9f2;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04);
  border-left-width: 4px;
}

.summary-tile--total   { border-left-color: #1f2a44; }
.summary-tile--active  { border-left-color: #16a07a; }
.summary-tile--issued  { border-left-color: #6d28d9; }

.summary-tile__label {
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #4b5563;
}

.summary-tile__value {
  font-size: 1.25rem;
  font-weight: 700;
  color: #1f2a44;
}

/* ── Table card ── */
.invoice-table {
  margin-bottom: 1rem;
}

/* ── Search ── */
.invoice-search-wrap {
  position: relative;
  width: 280px;
  max-width: 100%;
}

.invoice-search-icon {
  position: absolute;
  top: 50%;
  left: 0.75rem;
  transform: translateY(-50%);
  color: #6b7280;
  pointer-events: none;
}

.invoice-input {
  width: 100%;
  height: 34px;
  padding: 0.3rem 2.25rem 0.3rem 2.1rem;
  border: 1px solid #d8dde6;
  border-radius: 999px;
  background: #ffffff;
  font-size: 0.82rem;
  color: #2f3640;
  font-family: inherit;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.invoice-input:focus {
  outline: none;
  border-color: #16a07a;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.18);
}

.invoice-search-clear {
  position: absolute;
  top: 50%;
  right: 0.5rem;
  transform: translateY(-50%);
  width: 22px;
  height: 22px;
  border: 0;
  border-radius: 50%;
  background: #e5e9f2;
  color: #4b5563;
  font-size: 1rem;
  line-height: 1;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s ease, color 0.15s ease;
}

.invoice-search-clear:hover {
  background: #d1d5db;
  color: #1f2a44;
}

/* ── Empty state ── */
.invoice-empty {
  text-align: center;
  padding: 1.5rem 1rem;
}

.invoice-empty__title {
  font-size: 0.95rem;
  font-weight: 700;
  color: #1f2a44;
  margin-bottom: 0.25rem;
}

.invoice-empty__hint {
  font-size: 0.82rem;
  color: #6b7280;
}

.invoice-empty__hint code {
  background: #eef1f7;
  color: #1f2a44;
  padding: 0.05rem 0.35rem;
  border-radius: 4px;
  font-size: 0.78rem;
}

.invoice-empty__link {
  border: 0;
  background: transparent;
  padding: 0;
  color: #0e5b46;
  font-weight: 600;
  cursor: pointer;
}

.invoice-empty__link:hover {
  text-decoration: underline;
}

/* ── Cell content ── */
.series-name {
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
}

.series-pill {
  display: inline-flex;
  align-items: center;
  padding: 0.18rem 0.55rem;
  border-radius: 6px;
  font-size: 0.66rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  white-space: nowrap;
}

.series-pill--gray   { background: #f1f5f9; color: #475569; }
.series-pill--green  { background: #dff5ea; color: #0e5b46; }
.series-pill--blue   { background: #dbeafe; color: #1e40af; }
.series-pill--violet { background: #ede9fe; color: #5b21b6; }

.series-name__text {
  font-size: 0.85rem;
  font-weight: 600;
  color: #1f2a44;
  letter-spacing: -0.01em;
}

.prefix-chip,
.next-chip {
  display: inline-block;
  padding: 0.2rem 0.55rem;
  border-radius: 6px;
  background: #f1f5f9;
  color: #1f2a44;
  font-size: 0.78rem;
  font-weight: 600;
  font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
  letter-spacing: 0.01em;
}

.next-chip {
  background: #ecfdf5;
  color: #0e5b46;
}

.counter-value {
  font-size: 0.92rem;
  font-weight: 700;
  color: #1f2a44;
  letter-spacing: -0.01em;
  font-variant-numeric: tabular-nums;
}

.counter-cell {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  justify-content: flex-end;
}

.counter-flag {
  display: inline-flex;
  align-items: center;
  padding: 0.08rem 0.45rem;
  border-radius: 999px;
  background: #fee2e2;
  color: #991b1b;
  font-size: 0.66rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  white-space: nowrap;
}

.next-chip--collide {
  background: #fee2e2;
  color: #991b1b;
}

/* ── Edit button ── */
.edit-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  height: 28px;
  padding: 0 0.7rem;
  border-radius: 6px;
  border: 1px solid #e5e9f2;
  background: #ffffff;
  color: #1f2a44;
  font-size: 0.76rem;
  font-weight: 600;
  cursor: pointer;
  transition: border-color 0.15s ease, color 0.15s ease, background 0.15s ease, box-shadow 0.15s ease;
}

.edit-btn:hover {
  border-color: #16a07a;
  color: #0e5b46;
  background: #f5fbf8;
  box-shadow: 0 2px 4px rgba(14, 91, 70, 0.1);
}

/* ── Modal form ── */
.invoice-modal >>> .modal-content {
  border-radius: 12px;
  border: 1px solid #e5e9f2;
  box-shadow: 0 12px 32px rgba(15, 23, 42, 0.18);
}

.invoice-modal >>> .modal-header {
  border-bottom: 1px solid #eef1f7;
  padding: 0.95rem 1.25rem;
  background: #fbfcfe;
  border-radius: 12px 12px 0 0;
}

.invoice-modal >>> .modal-title {
  font-size: 0.98rem;
  font-weight: 700;
  color: #1f2a44;
  letter-spacing: -0.01em;
}

.invoice-modal >>> .modal-body {
  padding: 1.25rem;
}

.invoice-modal >>> .modal-footer {
  border-top: 1px solid #eef1f7;
  padding: 0.85rem 1.25rem;
  gap: 0.6rem;
  background: #fbfcfe;
  border-radius: 0 0 12px 12px;
}

.invoice-form {
  display: flex;
  flex-direction: column;
  gap: 1.05rem;
}

.invoice-form__meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.65rem 0.85rem;
  border-radius: 8px;
  background: #f5fbf8;
  border: 1px solid #c7ebda;
}

.invoice-form__meta-label {
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #0e5b46;
}

.invoice-form__meta-value {
  font-size: 0.88rem;
  font-weight: 700;
  color: #1f2a44;
}

.form-field {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}

.form-label {
  font-size: 0.8rem;
  font-weight: 600;
  color: #1f2a44;
  letter-spacing: -0.01em;
}

.form-input {
  width: 100%;
  height: 38px;
  padding: 0.3rem 0.85rem;
  border: 1px solid #d8dde6;
  border-radius: 8px;
  background: #ffffff;
  font-size: 0.85rem;
  color: #2f3640;
  font-family: inherit;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.form-input:focus {
  outline: none;
  border-color: #16a07a;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.18);
}

.form-input:disabled {
  background: #f8fafc;
  cursor: not-allowed;
}

.form-input.has-error {
  border-color: #dc2626;
}

.form-input.has-error:focus {
  box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.15);
}

.form-hint {
  font-size: 0.74rem;
  color: #6b7280;
}

.form-hint--error {
  color: #b91c1c;
  font-weight: 600;
}

.invoice-form__preview {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.65rem 0.85rem;
  border-radius: 8px;
  background: #fbfcfe;
  border: 1px dashed #d8dde6;
}

.invoice-form__preview-label {
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #4b5563;
}

.invoice-form__preview-value {
  display: inline-block;
  padding: 0.2rem 0.55rem;
  border-radius: 6px;
  background: #ecfdf5;
  color: #0e5b46;
  font-size: 0.85rem;
  font-weight: 700;
  font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
}

.invoice-form__audit {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.55rem 0.85rem;
  border-radius: 8px;
  background: #f1f5f9;
  border: 1px solid #e5e9f2;
}

.invoice-form__audit-label {
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #475569;
}

.invoice-form__audit-value {
  display: inline-block;
  padding: 0.18rem 0.5rem;
  border-radius: 6px;
  background: #ffffff;
  color: #1f2a44;
  font-size: 0.82rem;
  font-weight: 700;
  font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
}

.invoice-form__collide {
  padding: 0.6rem 0.85rem;
  border-radius: 8px;
  background: #fee2e2;
  border: 1px solid #fecaca;
  color: #991b1b;
  font-size: 0.78rem;
  line-height: 1.45;
}

.invoice-form__collide strong {
  color: #7f1d1d;
  font-weight: 700;
}

/* ── Modal buttons ── */
.cancel-btn {
  height: 36px;
  padding: 0 1rem;
  border-radius: 8px;
  border: 1px solid #e5e9f2;
  background: #ffffff;
  color: #1f2a44;
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}

.cancel-btn:hover:not(:disabled) {
  border-color: #cbd5e1;
  background: #f8fafc;
}

.cancel-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.submit-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  height: 36px;
  padding: 0 1.1rem;
  border-radius: 8px;
  border: 0;
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
  color: #ffffff;
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.18);
  transition: transform 0.15s ease, box-shadow 0.15s ease, filter 0.15s ease;
}

.submit-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 6px 14px rgba(14, 91, 70, 0.28);
  filter: brightness(1.05);
}

.submit-btn:active:not(:disabled) {
  transform: translateY(0);
  filter: brightness(0.96);
}

.submit-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
  box-shadow: none;
}

.submit-btn__spinner {
  width: 14px;
  height: 14px;
}

/* ── Responsive ── */
@media (max-width: 991px) {
  .summary-tiles {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

@media (max-width: 767px) {
  .invoice-toolbar {
    align-items: flex-start;
    flex-wrap: wrap;
  }
  .invoice-toolbar__title {
    flex: 1 1 100%;
  }
  .summary-tiles {
    grid-template-columns: 1fr;
  }
  .invoice-search-wrap {
    width: 100%;
  }
}

@media (max-width: 575px) {
  .invoice-page {
    padding: 0 0 0.5rem;
  }
  .invoice-toolbar {
    padding: 0.25rem 0 0.75rem;
    gap: 0.5rem;
  }
  .invoice-toolbar__accent {
    height: 30px;
  }
  .invoice-toolbar__title h1 {
    font-size: 1rem;
  }
  .invoice-toolbar__title p {
    font-size: 0.72rem;
  }
  .invoice-form {
    padding: 0.75rem;
  }
  .invoice-form__meta,
  .invoice-form__preview,
  .invoice-form__audit,
  .invoice-form__collide {
    flex-wrap: wrap;
    gap: 0.4rem;
  }
}
</style>
